import React, { useState } from 'react'
import DOMPurify from 'dompurify'
import {
  TabbedShowLayout,
  Tab,
  TextField,
  NumberField,
  ReferenceField,
  useRecordContext,
  useRefresh,
  useNotify,
  EditButton,
  DeleteButton,
} from 'react-admin'
import { Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import Show from '../common/views/Show'
import DateField from '../common/fields/DateField'
import { API_URL } from '../../services/settings'

const LeadDirectoryTitle = ({ record }) => {
  return <span>{record ? record.name : ''}</span>
}

const RichTextDisplay = ({ source }) => {
  const record = useRecordContext()
  if (!record || !record[source]) return null
  const html = DOMPurify.sanitize(record[source])
  return (
    <Box
      sx={{
        '& h1, & h2, & h3, & h4, & h5, & h6': { mt: 2, mb: 1 },
        '& p': { mb: 1 },
        '& ul, & ol': { pl: 2, mb: 1 },
      }}
      dangerouslySetInnerHTML={{ __html: html }}
    />
  )
}

const ActionButtons = () => {
  const record = useRecordContext()
  const refresh = useRefresh()
  const notify = useNotify()
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false)
  const [loading, setLoading] = useState(false)

  const handleArchive = async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`${API_URL}/lead_directories/${record.id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      })
      if (response.ok) {
        notify('Directory archived successfully', { type: 'success' })
        refresh()
        setOpenDeleteDialog(false)
        // Redirect back to list
        window.location.href = '/lead_directories'
      } else {
        notify('Failed to archive directory', { type: 'error' })
      }
    } catch (error) {
      notify('Error: ' + error.message, { type: 'error' })
    } finally {
      setLoading(false)
    }
  }

  if (!record) return null

  return (
    <Box sx={{ display: 'flex', gap: 1 }}>
      <EditButton record={record} />
      <Button
        variant="outlined"
        color="error"
        size="small"
        startIcon={<DeleteIcon />}
        onClick={() => setOpenDeleteDialog(true)}
      >
        Archive
      </Button>

      <Dialog open={openDeleteDialog} onClose={() => setOpenDeleteDialog(false)}>
        <DialogTitle>Archive Directory</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to archive "{record.name}"?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button
            onClick={handleArchive}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {loading ? 'Archiving...' : 'Archive'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

const CustomShowActions = () => {
  const record = useRecordContext()
  return (
    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
      <ActionButtons />
    </Box>
  )
}

const LeadDirectoryShow = (props) => (
  <Show {...props} title={<LeadDirectoryTitle />} actions={<CustomShowActions />}>
    <TabbedShowLayout>
      <Tab label="Summary">
        <TextField source="id" label="ID" />
        <TextField source="name" label="Name" />
        <TextField source="site" label="Site URL" />
        <TextField source="industry" label="Industry" />
        <TextField source="type" label="Type" />
        <NumberField source="estimated_leads_count" label="Estimated Leads Count" />
        <ReferenceField
          source="recommended_campaign_id"
          reference="campaigns"
          label="Recommended Campaign"
          link="show"
        >
          <TextField source="name" />
        </ReferenceField>
        <DateField source="created_at" label="Created At" />
        <DateField source="updated_at" label="Updated At" />
      </Tab>
      <Tab label="Guide">
        <RichTextDisplay source="guide" />
      </Tab>
      <Tab label="Details">
        <TextField source="description" label="Description" />
        <TextField source="tags" label="Tags" />
      </Tab>
    </TabbedShowLayout>
  </Show>
)

export default LeadDirectoryShow
